import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import FloatingHeader from '../layout/FloatingHeader';
import Footer from '../Footer';
import Lichtszene from '../licht/Lichtszene';
import { SEITEN_BLENDE } from '../animations/seitenBlende';
import './recht.css';

// Gemeinsamer Rahmen für Impressum, Datenschutz, AGB und Widerruf im neuen Design:
// Licht-Kopf wie die anderen Unterseiten, darunter links eine mitlaufende Liste der Abschnitte
// (wie beim Support), rechts der Rechtstext. Der Text selbst bleibt in den Seiten unverändert;
// die Abschnittsliste entsteht aus deren <h2>-Überschriften.
// Markiert ist der Abschnitt, dessen Überschrift zuletzt die Oberkante (Leiste + Luft) erreicht
// hat. Nach einem Klick in der Liste bleibt genau der angeklickte markiert, bis der Sprung
// zu Ende ist — auch wenn er (weit unten) gar nicht bis an die Oberkante kommt.
const OBERKANTE = 110;

export default function RechtsRahmen({ en, titel, unterzeile, children }) {
  const text = useRef(null);
  const sperre = useRef(null);
  const [abschnitte, setAbschnitte] = useState([]);
  const [aktiv, setAktiv] = useState(null);
  const navRef = useRef(null);

  // Lange Listen (Datenschutz: 17 Abschnitte) scrollen auf kleinen Laptops in sich: den markierten
  // Eintrag dann mitnehmen, damit er nicht unter der Kante verschwindet
  useEffect(() => {
    const nav = navRef.current;
    const el = nav && nav.querySelector('a.an');
    if (!el || nav.scrollHeight <= nav.clientHeight) return;
    const oben = el.offsetTop, unten = oben + el.offsetHeight;
    if (oben < nav.scrollTop + 40) nav.scrollTop = Math.max(0, oben - 40);
    else if (unten > nav.scrollTop + nav.clientHeight - 20) nav.scrollTop = unten - nav.clientHeight + 20;
  }, [aktiv]);

  useEffect(() => {
    const kopf = [...text.current.querySelectorAll('h2')];
    kopf.forEach((h, i) => { if (!h.id) h.id = `abschnitt-${i + 1}`; });
    setAbschnitte(kopf.map(h => ({ id: h.id, name: h.textContent.trim() })));
    let bild = 0;
    const pruefe = () => {
      bild = 0;
      // Ganz unten kommen die letzten Überschriften nicht mehr bis an die Oberkante:
      // dort zählt die letzte, die in der oberen Bildhälfte steht.
      const unten = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      let id = kopf[0] && kopf[0].id;
      kopf.forEach(h => {
        const oben = h.getBoundingClientRect().top;
        if (oben <= OBERKANTE + 20 || (unten && oben < window.innerHeight * 0.6)) id = h.id;
      });
      setAktiv(id);
    };
    const beimScrollen = () => {
      // Während eines Sprungs aus der Liste: nur das Ende abwarten (keine Bewegung mehr für 150 ms)
      if (sperre.current) { clearTimeout(sperre.current); sperre.current = setTimeout(() => { sperre.current = null; }, 150); return; }
      if (!bild) bild = requestAnimationFrame(pruefe);
    };
    pruefe();
    window.addEventListener('scroll', beimScrollen, { passive: true });
    window.addEventListener('resize', beimScrollen);
    return () => {
      window.removeEventListener('scroll', beimScrollen);
      window.removeEventListener('resize', beimScrollen);
      cancelAnimationFrame(bild);
      clearTimeout(sperre.current); sperre.current = null;
    };
  }, [en]);

  const springe = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    setAktiv(id);
    clearTimeout(sperre.current);
    sperre.current = setTimeout(() => { sperre.current = null; }, 150);
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - OBERKANTE, behavior: 'smooth' });
  };

  return (
    <motion.div {...SEITEN_BLENDE}>
      <FloatingHeader />
      <div className="rechtsseite" lang={en ? 'en' : 'de'}>
        <Lichtszene />
        <header className="recht-kopf">
          <span className="kicker">{en ? 'Legal' : 'Rechtliches'}</span>
          <h1>{titel}{unterzeile && <span className="grau">{unterzeile}</span>}</h1>
        </header>
        <div className="recht-raster">
          <nav className="recht-nav" ref={navRef} aria-label={en ? 'On this page' : 'Auf dieser Seite'}>
            <span className="kicker">{en ? 'On this page' : 'Auf dieser Seite'}</span>
            {abschnitte.map(a => (
              <a key={a.id} href={`#${a.id}`} className={a.id === aktiv ? 'an' : ''} onClick={e => springe(e, a.id)}>{a.name}</a>
            ))}
          </nav>
          <div className="recht-text" ref={text}>{children}</div>
        </div>
      </div>
      <div className="relative z-10"><Footer /></div>
    </motion.div>
  );
}
